import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface TypeaheadFilterProps<V extends string | number> {
  items: { value: V; label: string; color?: string | null }[];
  selectedValues: V[] | null;
  onSelect: (value: V) => void;
  onRemove: (value: V) => void;
  emptyMessage: string;
  placeholder?: string;
}

/** Returns white or black depending on background luminance */
function contrastText(hex: string): string {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#1a1a1a" : "#ffffff";
}

function TypeaheadFilter<V extends string | number>({
  items,
  selectedValues,
  onSelect,
  onRemove,
  emptyMessage,
  placeholder,
}: TypeaheadFilterProps<V>) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = selectedValues || [];
  const available = items.filter(
    (item) =>
      !selected.includes(item.value) &&
      item.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedItems = items.filter((item) => selected.includes(item.value));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4f00]"
        placeholder={placeholder || t("views.outreach.campaigns.filters.search")}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
      />

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {available.length > 0 ? (
            available.map((item, idx) => (
              <button
                key={`${String(item.value)}-${idx}`}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-[#ff4f00] hover:text-white transition-colors flex items-center gap-2"
                onClick={() => {
                  onSelect(item.value);
                  setSearch("");
                  setIsOpen(false);
                }}
              >
                {item.color && (
                  <span
                    className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                )}
                {item.label}
              </button>
            ))
          ) : (
            <p className="px-3 py-2 text-sm text-gray-400">{emptyMessage}</p>
          )}
        </div>
      )}

      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedItems.map((item) => {
            const hasColor = item.color && item.color.startsWith("#");
            const bgColor = hasColor ? item.color! : undefined;
            const textColor = hasColor ? contrastText(item.color!) : undefined;

            return (
              <span
                key={String(item.value)}
                className={
                  hasColor
                    ? "inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full border"
                    : "inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-800 text-sm rounded-full border border-orange-200"
                }
                style={
                  hasColor
                    ? { backgroundColor: bgColor, color: textColor, borderColor: bgColor }
                    : undefined
                }
              >
                {item.label}
                <button
                  type="button"
                  className={
                    hasColor
                      ? "font-bold ml-1 opacity-70 hover:opacity-100"
                      : "text-orange-500 hover:text-orange-700 font-bold ml-1"
                  }
                  style={hasColor ? { color: textColor } : undefined}
                  onClick={() => onRemove(item.value)}
                >
                  &times;
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TypeaheadFilter;
